'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Button 스타일 시스템 (variant × intent × size 3축)
 *
 * - variant: 시각 스타일 (solid/soft/outline/ghost/link)
 * - intent:  의미 색상 (primary/neutral/danger)
 * - size:    크기 (sm/md/lg/xl/2xl/3xl/4xl)
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        solid: '',
        soft: '',
        outline: 'border',
        ghost: 'bg-transparent',
        link: 'bg-transparent underline-offset-4 hover:underline !px-0 !h-auto',
      },
      intent: {
        primary: '',
        neutral: '',
        danger: '',
      },
      size: {
        sm: 'h-7 px-1 gap-1 text-xs rounded-md',
        md: 'h-8 px-2 gap-2 text-xs rounded-md',
        lg: 'h-9 px-3 gap-2 text-sm rounded-md',
        xl: 'h-11 px-4 gap-2 text-base rounded-lg',
        '2xl': 'h-12 px-5 gap-2 text-base rounded-lg',
        '3xl': 'h-14 px-6 gap-3 text-lg font-semibold rounded-xl',
        '4xl': 'h-16 px-8 gap-3 text-xl font-semibold rounded-xl',
      },
    },
    compoundVariants: [
      // solid: 채움 + 흰 텍스트 (또는 진한 텍스트)
      { variant: 'solid', intent: 'primary', class: 'bg-brand-500 text-white hover:bg-brand-600' },
      { variant: 'solid', intent: 'neutral', class: 'bg-gray-200 text-gray-700 hover:bg-gray-300' },
      { variant: 'solid', intent: 'danger', class: 'bg-danger-500 text-white hover:bg-danger-600' },

      // soft: 연한 배경 + 같은 계열 텍스트
      { variant: 'soft', intent: 'primary', class: 'bg-brand-50 text-brand-600 hover:bg-brand-100' },
      { variant: 'soft', intent: 'neutral', class: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
      { variant: 'soft', intent: 'danger', class: 'bg-danger-50 text-danger-600 hover:bg-danger-100' },

      // outline: 외곽선 + 텍스트
      { variant: 'outline', intent: 'primary', class: 'border-brand-500 text-brand-600 hover:bg-brand-50' },
      { variant: 'outline', intent: 'neutral', class: 'border-gray-300 text-gray-700 hover:bg-gray-50' },
      { variant: 'outline', intent: 'danger', class: 'border-danger-500 text-danger-600 hover:bg-danger-50' },

      // ghost: 배경 투명, hover 시만 연한 배경
      { variant: 'ghost', intent: 'primary', class: 'text-brand-600 hover:bg-brand-50' },
      { variant: 'ghost', intent: 'neutral', class: 'text-gray-700 hover:bg-gray-100' },
      { variant: 'ghost', intent: 'danger', class: 'text-danger-600 hover:bg-danger-50' },

      // link: 밑줄 텍스트
      { variant: 'link', intent: 'primary', class: 'text-brand-600 hover:text-brand-700' },
      { variant: 'link', intent: 'neutral', class: 'text-gray-700 hover:text-gray-900' },
      { variant: 'link', intent: 'danger', class: 'text-danger-600 hover:text-danger-700' },
    ],
    defaultVariants: {
      variant: 'solid',
      intent: 'primary',
      size: 'xl',
    },
  },
);

/** size별 아이콘 픽셀 크기 (광학적으로 텍스트와 밸런스) */
const iconSizeMap = {
  sm: 14,
  md: 16,
  lg: 16,
  xl: 20,
  '2xl': 20,
  '3xl': 24,
  '4xl': 28,
} as const;

/**
 * Legacy variant 이름 → 신규 API (variant + intent) 매핑.
 * 기존 149+ 호출 사이트가 마이그레이션되는 동안 임시 호환 레이어.
 * TODO: 마이그레이션 완료 후 제거
 */
type LegacyVariant = 'fill-blue' | 'blue' | 'fill-gray' | 'transparent' | 'red' | 'fill-red';
type LegacySize = 'xxl';

const LEGACY_VARIANT_MAP: Record<LegacyVariant, { variant: 'solid' | 'soft' | 'ghost'; intent: 'primary' | 'neutral' | 'danger' }> = {
  'fill-blue': { variant: 'solid', intent: 'primary' },
  blue: { variant: 'soft', intent: 'primary' },
  'fill-gray': { variant: 'solid', intent: 'neutral' },
  transparent: { variant: 'ghost', intent: 'neutral' },
  red: { variant: 'soft', intent: 'danger' },
  'fill-red': { variant: 'solid', intent: 'danger' },
};

type NewVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
type NewIntent = NonNullable<VariantProps<typeof buttonVariants>['intent']>;
type NewSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  variant?: NewVariant | LegacyVariant;
  intent?: NewIntent;
  size?: NewSize | LegacySize;
  /** 좌측 아이콘 (size에 맞춰 자동 크기 조정) */
  icon?: React.ReactNode;
  /** 로딩 중: icon 자리에 스피너 + disabled */
  isLoading?: boolean;
  /** Radix Slot 패턴 — 자식 엘리먼트에 스타일 투과 (Link/label 등) */
  asChild?: boolean;
}

/**
 * 프로젝트 공식 Button atom.
 * variant × intent × size 3축으로 조합.
 *
 * @example
 * <Button>저장</Button>                                 // solid primary xl (기본값)
 * <Button variant="outline" intent="neutral">취소</Button>
 * <Button variant="solid" intent="danger" size="md">삭제</Button>
 * <Button variant="ghost" intent="neutral" size="sm" icon={<Pencil />}>수정</Button>
 * <Button asChild><Link href="/login">로그인</Link></Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, intent, size, icon, children, isLoading, asChild, disabled, ...props },
    ref,
  ) => {
    // Legacy variant 자동 매핑 (backward compat)
    const legacy = typeof variant === 'string' && variant in LEGACY_VARIANT_MAP
      ? LEGACY_VARIANT_MAP[variant as LegacyVariant]
      : undefined;
    const resolvedVariant: NewVariant = legacy ? legacy.variant : ((variant as NewVariant) ?? 'solid');
    const resolvedIntent: NewIntent = legacy ? legacy.intent : (intent ?? 'primary');
    const resolvedSize: NewSize = size === 'xxl' ? '2xl' : ((size as NewSize) ?? 'xl');

    const iconSize = iconSizeMap[resolvedSize];

    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant: resolvedVariant, intent: resolvedIntent, size: resolvedSize, className }))}
          ref={ref as React.Ref<HTMLElement>}
          {...(props as React.HTMLAttributes<HTMLElement>)}
        >
          {children}
        </Slot>
      );
    }

    const leadingNode = isLoading ? (
      <Loader2 className="animate-spin" width={iconSize} height={iconSize} />
    ) : icon && React.isValidElement(icon) ? (
      React.cloneElement(
        icon as React.ReactElement<{ width?: number; height?: number }>,
        { width: iconSize, height: iconSize },
      )
    ) : (
      icon
    );

    return (
      <button
        type="button"
        className={cn(buttonVariants({ variant: resolvedVariant, intent: resolvedIntent, size: resolvedSize, className }))}
        disabled={disabled || isLoading}
        ref={ref}
        {...props}
      >
        {leadingNode}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

export default Button;
